/**
 * The one delay control, used by BOTH the wizard's step 1 and the edit-campaign
 * dialog so the two can never drift.
 *
 * Everything is edited in minutes (the canonical unit); the unit dropdown is
 * presentation only and converts both ways. `delay_hours` is written alongside
 * `delay_minutes` purely to keep the legacy column in sync.
 */
import React from 'react'
import { Field, Input, Radio, Select, Switch } from '@/components/ds'
import {
  MAX_DELAY_MINUTES,
  MIN_DELAY_MINUTES,
  delayEnabled,
  explainDelay,
  fixedDelayMinutes,
  randomWindowMinutes,
} from '@shared/schedule'
import type { DelayMode } from '@shared/types'

export interface DelayValue {
  delay_enabled: boolean
  delay_mode: DelayMode
  delay_minutes: number | null
  delay_hours: number
  random_window_minutes: number
}

type Unit = 'minutes' | 'hours'

const UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
]

/** Minutes -> the value shown in the number box for the chosen unit. */
function toDisplay(minutes: number, unit: Unit): string {
  if (unit === 'hours') {
    const h = minutes / 60
    // keep it readable: 90 -> 1.5, 120 -> 2
    return String(Number.isInteger(h) ? h : Math.round(h * 100) / 100)
  }
  return String(minutes)
}

/** Whatever is typed -> canonical minutes. */
function toMinutes(raw: string, unit: Unit): number {
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.round(unit === 'hours' ? n * 60 : n)
}

export function DelayControl({
  value,
  onChange,
}: {
  value: DelayValue
  onChange: (next: DelayValue) => void
}) {
  const fixedMin = fixedDelayMinutes(value)
  const randomMin = randomWindowMinutes(value)

  // Default to Minutes; start in Hours only when the stored value is a clean
  // multiple of an hour and large enough that hours read better.
  const [fixedUnit, setFixedUnit] = React.useState<Unit>(
    fixedMin >= 60 && fixedMin % 60 === 0 ? 'hours' : 'minutes',
  )
  const [randomUnit, setRandomUnit] = React.useState<Unit>(
    randomMin >= 60 && randomMin % 60 === 0 ? 'hours' : 'minutes',
  )

  const [fixedText, setFixedText] = React.useState(() => toDisplay(fixedMin, fixedUnit))
  const [randomText, setRandomText] = React.useState(() => toDisplay(randomMin, randomUnit))

  const on = delayEnabled(value)

  function commitFixed(raw: string, unit: Unit) {
    const minutes = toMinutes(raw, unit)
    onChange({
      ...value,
      delay_minutes: minutes,
      delay_hours: minutes / 60, // keep the legacy column in step
    })
  }

  function commitRandom(raw: string, unit: Unit) {
    const minutes = Math.max(1, toMinutes(raw, unit))
    onChange({ ...value, random_window_minutes: minutes })
  }

  /**
   * Switching the delay ON must land on something the 5-minute floor accepts —
   * the stored numbers are very often 0 (or the old 1-minute window), and
   * revealing the controls pre-filled with a value that fails validation reads
   * as the toggle itself being broken.
   */
  function toggle(next: boolean) {
    if (!next) {
      onChange({ ...value, delay_enabled: false })
      return
    }
    const fixedOk = Math.max(MIN_DELAY_MINUTES, fixedMin)
    const randomOk = Math.max(MIN_DELAY_MINUTES, randomMin)
    setFixedText(toDisplay(fixedOk, fixedUnit))
    setRandomText(toDisplay(randomOk, randomUnit))
    onChange({
      ...value,
      delay_enabled: true,
      delay_minutes: fixedOk,
      delay_hours: fixedOk / 60,
      random_window_minutes: randomOk,
    })
  }

  function switchFixedUnit(unit: Unit) {
    setFixedUnit(unit)
    setFixedText(toDisplay(fixedMin, unit)) // convert the shown number, not the stored one
  }

  function switchRandomUnit(unit: Unit) {
    setRandomUnit(unit)
    setRandomText(toDisplay(randomMin, unit))
  }

  const bound = (m: number, label: string) =>
    m < MIN_DELAY_MINUTES
      ? `Minimum ${label} is ${MIN_DELAY_MINUTES} minutes — turn Delay off to send straight away`
      : m > MAX_DELAY_MINUTES
        ? `Maximum ${label} is 30 days (${MAX_DELAY_MINUTES} minutes)`
        : undefined

  const fixedError = on ? bound(fixedMin, 'delay') : undefined
  const randomError = on ? bound(randomMin, 'window') : undefined

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>Delay</div>
        <Switch checked={on} onChange={toggle} label={on ? 'On' : 'Off'} />
      </div>

      {/* Off is not a disabled state — there is nothing to configure, so the
          controls are gone rather than greyed. */}
      {!on ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{explainDelay(value)}</div>
      ) : (
        <>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Radio
          checked={value.delay_mode === 'random_1h'}
          onChange={() => onChange({ ...value, delay_mode: 'random_1h' })}
          label="Random, up to"
        />
        <Radio
          checked={value.delay_mode === 'fixed_hours'}
          onChange={() => onChange({ ...value, delay_mode: 'fixed_hours' })}
          label="Fixed"
        />
      </div>

      {value.delay_mode === 'random_1h' ? (
        <Field
          error={randomError}
          hint={randomError ? undefined : 'Spreads a batch out instead of sending it all at once.'}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 110 }}>
              <Input
                type="number"
                min={MIN_DELAY_MINUTES}
                step={1}
                value={randomText}
                error={randomError}
                onChange={(v) => {
                  setRandomText(v)
                  commitRandom(v, randomUnit)
                }}
              />
            </div>
            <div style={{ width: 118 }}>
              <Select
                options={UNITS}
                value={randomUnit}
                onChange={(u) => {
                  switchRandomUnit(u as Unit)
                }}
              />
            </div>
          </div>
        </Field>
      ) : (
        <Field
          error={fixedError}
          hint={fixedError ? undefined : `Minimum ${MIN_DELAY_MINUTES} minutes while Delay is on.`}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 110 }}>
              <Input
                type="number"
                min={MIN_DELAY_MINUTES}
                step={1}
                value={fixedText}
                error={fixedError}
                onChange={(v) => {
                  setFixedText(v)
                  commitFixed(v, fixedUnit)
                }}
              />
            </div>
            <div style={{ width: 118 }}>
              <Select
                options={UNITS}
                value={fixedUnit}
                onChange={(u) => {
                  switchFixedUnit(u as Unit)
                }}
              />
            </div>
          </div>
        </Field>
      )}

          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{explainDelay(value)}</div>
        </>
      )}
    </div>
  )
}
